import { CommonDialogs } from "@mcleod/common";
import {
    Button, ContextMenuLabelProps, DropdownItem, Label, Layout, Panel, ScreenStack, Snackbar, Table, TableColumn, TableRow
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { Api, ModelRow, PermissionsUtil, StringUtil, ActionPermission, CompanySettings } from "@mcleod/core";
import { MultiSendRateConfirmation } from "@mcleod/dispatch/src/planning/MultiSendRateConfirmation";
import { OrderMultiSelectErrorModal } from "./OrderMultiSelectErrorModal";
import { BatchRatingResults, DispatchSlideout, DispatchUtil, ModelMovement } from "@mcleod/dispatch";
import { CarrierAssignmentStepper, P44ShipmentTracking, SendRateConfirmation } from "@mcleod/powerbroker";
import { MultiCarrierAssignmentStepper } from "@mcleod/powerbroker/src/MultiCarrierAssignmentStepper";
import { Fields, MultiUpdateFieldPrompt } from "@mcleod/dispatch/src/MultiUpdateFieldPrompt";
import { getDispatchControlValue } from "./models/ModelDispatchControl";

export class CommonContextMenuItems {

    public static MAX_ROWS = "Max rows exceeded for this action";

     public static createSendRateConfirmationItemFromSelected(selectedRows: TableRow[]): Partial<ContextMenuLabelProps> {
        let visible = selectedRows.every(row => ["C", "P", "D"].includes(row.data.get("move_status")));
        const isMultiSelect = selectedRows.length > 1;
        const isMultiSelectDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTI_RATE_CON);
        const exceedsSelectionMax = selectedRows.length > getDispatchControlValue("max_assign_selections")
        if ((isMultiSelect && isMultiSelectDenied) || exceedsSelectionMax) {
            visible = false;
        }

        return {
            imageName: ImageName.messagesOutgoingOutlined,
            text: "Send Rate Confirmation",
            visible: visible,
            onClick: () => {
                if (selectedRows.length > 1) {
                    MultiSendRateConfirmation.showSlideout(selectedRows);
                }
                else {
                    SendRateConfirmation.showSendRateConfirmationSlideout(null, selectedRows[0].data.get("movement_id"), null);
                }
            }
        }
    }

    public static createAssignCarrierFromSelected(selectedRows: TableRow[], buttonAssign: Button ): Partial<ContextMenuLabelProps> {
        let visible = false
        if (selectedRows.length !== 1) {
            visible = DispatchUtil.areMultiAssignable(selectedRows, buttonAssign) && !(selectedRows.length > getDispatchControlValue("max_assign_selections"));

        } else if (selectedRows?.[0].data) {
            visible = DispatchUtil.isAssignable(selectedRows[0].data, { btnAssign: buttonAssign  });
        }
        return {
            imageName: ImageName.exit,
            text: "Assign Carrier",
            visible: visible,
            onClick: () => {this.buttonAssignOnClick(buttonAssign, selectedRows)}
        }
    }

    public static createDispatchCarrierFromSelected(selectedRows: TableRow[], buttonDispatch: Button): Partial<ContextMenuLabelProps> {
        let visible = false
        if (selectedRows.length == 1) {
            visible = DispatchUtil.canDispatch(selectedRows[0].data, buttonDispatch);
        }

        return {
            imageName: ImageName.send,
            text: "Dispatch",
            visible: visible,
            onClick: () => this.buttonDispatchOnClick(selectedRows[0], buttonDispatch)
        }
    }

    static buttonDispatchOnClick(selectedRow: TableRow, buttonDispatch: Button ) {
        const modelRow = selectedRow?.data;
        if (modelRow == null)
            return;

        buttonDispatch.busy = true;
        this.showDispatchSlideout(selectedRow).finally(() => buttonDispatch.busy = false);
    }

    static async showDispatchSlideout(row: TableRow, previousStatus?: String, eventDrivenCallin?: boolean, current_stop_id?: string) {
        const modelRow = row.data;
        const isBrokerageLtlOrder = modelRow.getBoolean("is_brokerage_ltl");
        const dispatchType = (await Api.post("lme/dispatch/movement-dispatch-type", { movement_id: modelRow.get("movement_id") })).data[0]?.["dispatch_type"];
        await (isBrokerageLtlOrder && StringUtil.equalsIgnoreCase(dispatchType, "P44") ? P44ShipmentTracking : DispatchSlideout).showSlideout(modelRow.get("movement_id"), {
            movementOrId: modelRow.get("movement_id"),
            orderId: modelRow.get("order_id"),
            customerId: modelRow.get("customer_id"),
            orderOnHold: modelRow.getBoolean("hold", false),
            originCity: modelRow.get("origin_city"),
            originState: modelRow.get("origin_state"),
            destinationCity: modelRow.get("destination_city"),
            destinationState: modelRow.get("destination_state"),
            eventDrivenCallin: eventDrivenCallin,
            currentStopID: current_stop_id,
            manifestID: modelRow.get("manifest_id"),
            doAfterSlideOut: (decorator, saved, current_stop_id) => {
                if (saved && !(isBrokerageLtlOrder && StringUtil.equalsIgnoreCase(dispatchType, "P44"))) {
                    const prevBrokerageStatus = previousStatus ?? modelRow.get("brokerage_status");
                    new ModelMovement().searchSingle({ id: modelRow.get("movement_id") }, "lme/powerbroker/BrokeragePlanningToolPanel").then((result) => {
                        if (prevBrokerageStatus !== result.get("brokerage_status") &&
                            result.get("brokerage_status.display_callin_screen") == "Y") {
                            this.showDispatchSlideout(row, result.get("brokerage_status"), true, current_stop_id);
                        }
                    });
                }
            }
        });
    }

    public static buttonAssignOnClick(buttonAssign: Button, selectedRows: TableRow[]) {
        buttonAssign.busy = true;
        if (selectedRows.length > 1) {
            MultiCarrierAssignmentStepper.showAsSlideout(selectedRows, buttonAssign);
        }
        else {
            const selectedRow = selectedRows[0];
            const modelRow = selectedRow.data;
            if (modelRow != null) {
                selectedRow.expanded = false;
                CarrierAssignmentStepper.showAsSlideout({
                    movementId: modelRow.get("movement_id"),
                    orderId: modelRow.get("order_id"),
                    carrierAssignmentButton: buttonAssign,
                    manifestId: modelRow.get("manifest_id")
                });
            }
            buttonAssign.busy = false;
        }
    }

    public static createRecentChangesItemFromSelected(selectedRows: TableRow[]): Partial<ContextMenuLabelProps> {
        if (selectedRows.length !== 1) {
            return {visible: false};
        }
        return CommonContextMenuItems.createRecentChangesItemFromTableRow(selectedRows[0]);
    }

    public static createRecentChangesItemFromTableRow(tableRow: TableRow): Partial<ContextMenuLabelProps> {
        return CommonContextMenuItems.createRecentChangesItem( tableRow.data as ModelRow);
    }

    public static createRecentChangesItem(modelRow: ModelRow): Partial<ContextMenuLabelProps> {
        return {
            imageName: ImageName.list,
            text: "Recent Changes",
            visible: true,
            onClick: async () => {
                const rows=[] as ModelRow[];
                if( modelRow.recentChanges != null){
                    for( const change of modelRow.recentChanges){
                        rows.push(new ModelRow(null,false, change))
                    }
                }
                const tableFields = new Table({ headerVisible: false, fillHeight: true, padding: 0, rowBorderBottomWidth: 0, marginBottom: 12 } );
                tableFields.addColumn(new TableColumn({ headingDef: { caption: "Timestamp" }, cell: { field: "timestamp" } }), true, false);
                tableFields.addColumn(new TableColumn({ headingDef: { caption: "Field" }, cell: { field: "fieldName" } }), true, false);
                tableFields.addColumn(new TableColumn({ headingDef: { caption: "Message old value" }, cell: { field: "incomingOldValue" } }), false, false);
                tableFields.addColumn(new TableColumn({ headingDef: { caption: "Screen old value" }, cell: { field: "oldValue" } }), false, false);
                tableFields.addColumn(new TableColumn({ headingDef: { caption: "New value" }, cell: { field: "newValue" } }), false, true);
                tableFields.displayData(rows[0], rows, 0);
                CommonDialogs.showDialog(tableFields, { title: "Recent changes" , width: "1100", height: 400, resizable: true });
            }
        }
    }

    private static hasUpdateFieldPermission(selectedRows: TableRow[]): boolean {
        return !(selectedRows.length > 1 && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTI_UPDATE_FIELDS))
    }

    private static rowsAllowedForUpdateField(selectedRows: TableRow[]): boolean {
        const updateFieldFilter = (row: TableRow) => {
            const isNotManifest = row.data.isNull("manifest_id");

            return isNotManifest
            && this.hasUpdateOrderPermissions(row.data)
        }
        return selectedRows.every(row => updateFieldFilter(row))
    }

    public static createUpdateFieldItemFromSelected(selectedRows: TableRow[], isCsm = false): Partial<ContextMenuLabelProps> {
        return {
            imageName: ImageName.bulkUpdate,
            text: "Update Field",
            visible: this.hasUpdateFieldPermission(selectedRows) && this.rowsAllowedForUpdateField(selectedRows)
                && !(selectedRows.length > getDispatchControlValue("max_update_selections")),
            onClick: () => this.buttonUpdateFieldOnClick(selectedRows, isCsm)
        }
    }

    public static syncButtonUpdateField(button: Button, selectedRows: TableRow[]) {
        if (!this.hasUpdateFieldPermission(selectedRows)) {
            button.disabledTooltip = "Only one order can be selected for this action.";
            button.enabled = false;
        }
        if (!this.rowsAllowedForUpdateField(selectedRows)) {
            button.disabledTooltip = "One or more selected Movements are invalid for this action";
            button.enabled = false;
        }
        if (selectedRows.length > getDispatchControlValue("max_update_selections")) {
            button.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            button.enabled = false;
        }
    }

    public static async buttonUpdateFieldOnClick(selectedRows: TableRow[], isCsm = false, allowedFields: string[] = []) {
        const layout = Layout.getLayout("lme/dispatch/MultiUpdateFieldPrompt") as MultiUpdateFieldPrompt;
        if (allowedFields.length == 0) {
            allowedFields = Object.values(Fields);
        }
        if (CompanySettings.getSingleton().isBrokerage() && !CompanySettings.getSingleton().isBoth())
            allowedFields = allowedFields.filter((field: string) => "movement.brokerage" !== field);
        layout.addLayoutLoadListener(() => {
            if (allowedFields.length > 0) {
                layout.textboxField.items = (layout.textboxField.items as DropdownItem[]).filter(
                    item => allowedFields.includes(item.value));
            }
            if (isCsm) {
                const item = (layout.textboxField.items as DropdownItem[]).find(item => item.value === "movement.dispatcher_user_id");
                if (item) item.displayValue = "Carrier Sales Rep/Dispatcher";
            }
        });
        layout.selectedData = selectedRows.map(row => row.data);
        return CommonDialogs.showDialog(layout, {
            title: `Update Field (${selectedRows.length} ${selectedRows.length > 1 ? "records" : "record"})`,
            panelTitleProps:{
                backgroundColor: "primary.darker",
                color: "primary.reverse"
            },
            okProps: {
                onClick: () => {
                    if (layout.validateSimple()) {
                        if (layout.switchFieldValue.visible && !layout.switchFieldValue.enabled)
                            return;

                        const { field, value } = layout.getResults();
                        return this.updateField(selectedRows, field, value);
                    }
                }
            }
        })
    }

    public static updateField(selectedRows: TableRow[], field: string, value: any) {
        const downloadSnackbar = Snackbar.showDownloadSnackbar(
            "Multi-Select Action in Progress", `Updating ${selectedRows.length} movement(s)...`, {
            persist: true,
            targetPanel: ScreenStack.getSnackbarTarget()
        });
        return Api.post("lme/dispatch/update-field", {
            field,
            value,
            record_ids: selectedRows.map(row => ({
                movement_id: row.data.get("movement_id"),
                order_id: row.data.get("order_id")
            }))
        }).then(output => {
            const results = output.data;
            const successCount = results.filter(result => result.success).length;
            const failedRows = [];
            const errorRows = [];
            const isOpsMgmt = "tableAvailableMoves" === selectedRows[0].table.id
            results.forEach(result => {
                const row = selectedRows.find(row => row.data.get("movement_id") === result.movement_id);
                if (!StringUtil.isEmptyString(result.error)) {
                    if (!result.success && result.retry) {
                        row.data.set("error", result.error);
                        failedRows.push(row);
                    } else {
                        const errorRow = row.data.createBasicCopy();
                        errorRow.set("error", result.error);
                        errorRows.push(errorRow);
                    }
                }
            });
            OrderMultiSelectErrorModal.showModal(failedRows, errorRows, successCount, "Update Field", "Retry",
                (retryRows) => this.updateField(retryRows, field, value), undefined, isOpsMgmt
            );
        }).finally(() => {
            if (selectedRows.length > 1) {
                selectedRows[0].table.selectedRows = [];
            }
            downloadSnackbar.dismiss();
        });
    }

    public static createRateOrdersItemFromSelected(selectedRows: TableRow[]): Partial<ContextMenuLabelProps> {
        return {
            imageName: ImageName.rateThisOrder,
            text: "Rate Order(s)",
            visible: this.hasRateOrdersPermission(selectedRows) && this.rowsAllowedForRateOrders(selectedRows)
                && !(selectedRows.length > getDispatchControlValue("max_rate_selections")),
            onClick: () => this.buttonRateOrdersOnClick(selectedRows)
        }
    }

    public static syncButtonRateOrders(button: Button, selectedRows: TableRow[]) {
        if (!this.hasRateOrdersPermission(selectedRows)) {
            button.disabledTooltip = "Only one order can be selected for this action.";
            button.enabled = false;
        }
        if (!this.rowsAllowedForRateOrders(selectedRows)) {
            button.disabledTooltip = "One or more selected Movements are invalid for this action";
            button.enabled = false;
        }
        if (selectedRows.length > getDispatchControlValue("max_rate_selections")) {
            button.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            button.enabled = false;
        }
    }

    private static rowsAllowedForRateOrders(selectedRows: TableRow[]): boolean {
        const rateOrderFilter = (row: TableRow) => {
            const isNotTransferredToBilling = row.data.isNull("transfer_to_billing_date")

            return isNotTransferredToBilling
            && this.hasUpdateMovementPermissions(row.data)
            && this.hasUpdateOrderPermissions(row.data)
        };
        return selectedRows.every(row => rateOrderFilter(row))
    }

    public static buttonRateOrdersOnClick(selectedRows: TableRow[]): Promise<void> {
        return CommonDialogs.showYesNo("Are you sure you want to rate the selected order(s)?","Rate Order(s)")
        .then((result) => (!!result) ? this.rateOrders(selectedRows) : Promise.resolve());
    }

    private static async rateOrders(selectedRows: TableRow[]): Promise<void> {
        const downloadSnackbar = Snackbar.showDownloadSnackbar(
            "Multi-Select Action in Progress", `Updating ${selectedRows.length} rate(s)...`, {
            persist: true,
            targetPanel: ScreenStack.getSnackbarTarget()
        });
        return Api.post("lme/dispatch/batch-rating", {
            order_ids: selectedRows.map(row => row.data.getString("order_id", null)).filter(id => !!id)
        }).then(output => {
            const results = output.data;
            const successCount = results.filter(result => result.success).length;
            const failedRows = [];
            const errorRows = [];
            results.forEach(result => {
                const row = selectedRows.find(row => row.data.get("order_id") === result.order_id);
                if (!StringUtil.isEmptyString(result.error)) {
                    if (!result.success && result.retry) {
                        row.data.set("error", result.error);
                        failedRows.push(row);
                    } else {
                        const errorRow = row.data.createBasicCopy();
                        errorRow.set("error", result.error);
                        errorRows.push(errorRow);
                    }
                }
            });

            OrderMultiSelectErrorModal.showModal(failedRows, errorRows, successCount, "Rate Order(s)", "Retry",
                (retryRows) => this.rateOrders(retryRows),
                (successCount > 0) ? [this.buildRateOrderResults(selectedRows, results)] : [],
                false
            );
        }).finally(() => {
            if (selectedRows.length > 1) {
                selectedRows[0].table.selectedRows = [];
            }
            downloadSnackbar.dismiss();
        });
    }

    static buildRateOrderResults(selectedRows: TableRow[], results: any[]) {
        const changedValueFilter = (result: any) => {
            return result.success && (
                (result.freight_charge.amount != result.updated_freight_charge.amount)
                || (result.other_charges.amount != result.updated_other_charges.amount)
                || (result.total_charges.amount != result.updated_total_charges.amount)
                || (result.target_pay.amount != result.updated_target_pay.amount)
                || (result.max_pay.amount != result.updated_max_pay.amount)
            );
        }
        const changedValues = results.filter(changedValueFilter);
        const panelRateResults = new Panel({id: "panelRateResults", fillRow: true});
        panelRateResults.add(new Label({id: "labelRateResults", text:`${changedValues.length} Rate(s) Changed`, rowBreak: false}));
        if (changedValues.length > 0) {
            panelRateResults.add(new Label({id: "labelRateResultsPipe", text:"|", rowBreak: false}));
            const labelViewChanges = new Label({color: "primary", fontBold: true, id: "labelViewChanges", text: "View Changes"});
            labelViewChanges.addClickListener(() => BatchRatingResults.showSlideout(selectedRows, changedValues));
            panelRateResults.add(labelViewChanges);
        }
        return panelRateResults;
    }

    private static hasRateOrdersPermission(selectedRows: TableRow[]): boolean {
        return !(selectedRows.length > 1 && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTISELECT_RATE_ORDERS))
    }

    public static hasUpdateMovementPermissions(modelRow: ModelRow): boolean {
        const isNotDelivered = modelRow.getString("move_status", null) !== "D"
        const isNotTransferred: boolean = modelRow.isNull("xfer2settle_date");
        const isNotBilled: boolean = modelRow.isNull("orders.bill_date");
        const updateSettledAllowed: boolean = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED);
        const updateDeliveredMoveAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);

        return (isNotDelivered || updateDeliveredMoveAllowed)
                && ((isNotTransferred && isNotBilled) || updateSettledAllowed);
    }

    public static hasUpdateOrderPermissions(modelRow: ModelRow): boolean {
        const isNotDelivered = modelRow.getString("move_status", null) !== "D";
        const isNotBilled = modelRow.isNull("transfer_to_billing_date");
        const updateDeliveredAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER);
        const updateBilledAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER);

        return (isNotDelivered || updateDeliveredAllowed)
                && (isNotBilled || updateBilledAllowed);
    }
}
